import { DOCUMENT } from '@angular/common';
import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { Inject } from '@angular/core';

interface LoadedScript {
  id: string;
  element: HTMLScriptElement;
  loaded: boolean;
  src: string;
}

export interface LoadScriptOptions {
  url: string;
  id?: string;
  forceReload?: boolean;
}

@Injectable({ providedIn: 'root' })
export class DynamicScriptLoaderService {
  readonly #scripts: Map<string, LoadedScript> = new Map<
    string,
    LoadedScript
  >();
  readonly #renderer: Renderer2;
  readonly #document: Document;

  constructor(
    factory: RendererFactory2, // Removed private for constructor param as it's only used to set #renderer
    @Inject(DOCUMENT) document: Document // document can be readonly
  ) {
    this.#renderer = factory.createRenderer(null, null);
    this.#document = document;
  }

  loadScript({
    url,
    id = url,
    forceReload = false,
  }: LoadScriptOptions): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const existingScript = this.#scripts.get(id);
      if (existingScript && !forceReload) {
        if (existingScript.loaded) {
          resolve();
        } else {
          this._createScriptElement(id, url, resolve, reject);
        }

        return;
      }

      if (existingScript && forceReload) {
        this._removeScript(id);
      }

      this._createScriptElement(id, url, resolve, reject);
    });
  }

  unloadScript(id: string): boolean {
    return this._removeScript(id);
  }

  isScriptLoaded(id: string): boolean {
    const script = this.#scripts.get(id);
    return script ? script.loaded : false;
  }

  private _removeScript(id: string): boolean {
    const script = this.#scripts.get(id);
    if (script && script.element) {
      this.#renderer.removeChild(this.#document.head, script.element);
      this.#scripts.delete(id);
      return true;
    }

    return false;
  }

  private _createScriptElement(
    id: string,
    url: string,
    resolve: () => void,
    reject: (reason?: unknown) => void
  ): void {
    const scriptElement: HTMLScriptElement =
      this.#renderer.createElement('script');
    scriptElement.type = 'text/javascript';
    scriptElement.src = url;
    scriptElement.async = true;
    scriptElement.defer = true;
    scriptElement.id = id;
    const scriptItem: LoadedScript = {
      id,
      element: scriptElement,
      loaded: false,
      src: url,
    };
    this.#scripts.set(id, scriptItem);
    scriptElement.onload = () => {
      const scriptEntry = this.#scripts.get(id);
      if (scriptEntry) {
        scriptEntry.loaded = true;
      }

      resolve();
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    scriptElement.onerror = (error: any) => {
      this._removeScript(id);
      reject(error);
    };
    this.#renderer.appendChild(this.#document.head, scriptElement);
  }
}
